import dayjs from 'dayjs';
import { Dispatch } from '@reduxjs/toolkit';
import { ContractState, convertCurrencyToSign } from '@received/pricing-model';
import { Cell, LogoCell, PaymentCardCell, StatusCol } from '../../../components/_uiComponents/Table/Table.utils';
import { images } from '../../../constants/images';
import { SubscribersView } from '../../../types/subscriptionTypes';
import { DATE_FORMAT } from '../../../constants/templateConstants';
import { Contract } from '../../../types/contractTypes';
import { MenuOption } from '../../../components';
import { PathsConfig, httpService } from '../../../services';
import { setOpenSuccessErrorModal } from '../../../storeSlices/errorSuccessSlice';
import { netTermsToLabel } from '../../../utils/GeneralUtils';
import { priceNumberWithCurrencySignDisplay } from '../../../utils/NumberUtils';
import { billingCycleLabelsByEnum } from '../../../constants/generalConstants';
import { subscriptionStateColor } from '../../../pages/PricingModels/PricingModelTabs/SubscriptionsTab/SubscriptionsTab.utils';
import { cancelContract, deleteContract } from '../../../pages/Contracts/ContractComponent/Contract.utils';
import { contractIdFormat } from '../../../utils/ContractUtils';
import styles from './SubscriptionsTab.module.scss';

export const subscriptionsTabTableHeader = (t: any) => [
	{
		headerTitle: 'subscription',
		accessor: 'subscriptionName',
		isSortable: true,
		CustomComponent: (item: SubscribersView) => (
			<LogoCell
				overrideStyles={{ avatarContainer: styles.avatarContainer }}
				title={item?.subscriptionName}
				logoData={images.subscriptions}
				subTitle={`${t('billing')}  ${
					item.billingCycles.length > 1
						? `${billingCycleLabelsByEnum[item.billingCycles[0]]} + ${item.billingCycles.length - 1}`
						: billingCycleLabelsByEnum[item.billingCycles[0]]
				}`}
			/>
		),
	},
	{
		headerTitle: 'id',
		accessor: 'contractNumber',
		CustomComponent: (item: SubscribersView) => <Cell accessor={contractIdFormat(item?.poNumber, item?.contractNumber)} />,
	},
	{
		headerTitle: 'products',
		accessor: 'products',
		isSortable: true,
		CustomComponent: (item: SubscribersView) => (
			<Cell
				accessor={item?.products?.length ? `${item?.products[0].name}${item?.products.length > 1 ? ` + ${item?.products.length - 1}` : ''}` : '-'}
				subAccessor={netTermsToLabel(item?.products[0].netTerms)}
			/>
		),
	},
	{
		headerTitle: 'paymentMethod',
		accessor: 'paymentMethod',
		CustomComponent: (item: SubscribersView) => <PaymentCardCell stripeCard={item?.paymentGatewayCustomer} />,
	},
	{
		headerTitle: 'nextInvoiceOn',
		accessor: 'nextInvoiceDate',
		CustomComponent: (item: SubscribersView) => (
			<Cell
				accessor={item?.nextInvoiceDate ? dayjs(item.nextInvoiceDate).format(DATE_FORMAT) : '-'}
				subAccessor={
					item?.nextInvoiceAmount &&
					`${t('for')} ${priceNumberWithCurrencySignDisplay(parseInt(item?.nextInvoiceAmount), convertCurrencyToSign(item?.currency))}`
				}
			/>
		),
	},
	{
		headerTitle: 'started',
		accessor: 'started',
		CustomComponent: (item: SubscribersView) => (
			<Cell accessor={dayjs(item.started).format(DATE_FORMAT)} subAccessor={`${item.startedInMonths}  ${t('monthAgo')}`} />
		),
	},
	{
		headerTitle: 'renewalOn',
		accessor: 'renewalOn',
		CustomComponent: (item: SubscribersView) => (
			<Cell
				accessor={item?.renewalOn ? dayjs(item.renewalOn).format(DATE_FORMAT) : '-'}
				subAccessor={item.renewalDaysLeft != null && item.renewalDaysLeft >= 0 ? `${item.renewalDaysLeft} ${t('daysLeft')}` : ''}
			/>
		),
	},
	{
		headerTitle: 'status',
		accessor: 'status',
		CustomComponent: (data: SubscribersView) => {
			const badgeColor = subscriptionStateColor(data);
			return <StatusCol color={badgeColor.color}>{badgeColor.text}</StatusCol>;
		},
	},
];

export const subscriptionsMenuOptions = (
	row: SubscribersView | Contract,
	t: any,
	dispatch: Dispatch,
	onRefresh?: () => void,
	onDelete?: () => void,
): MenuOption[] => {
	// A subscription is backed by a contract, so cancel/delete reuse the contract flows.
	// SubscribersView exposes the contract id as `contractId`, Contract as `id`.
	const subscriptionId = (row as SubscribersView)?.contractId || (row as Contract)?.id || '';
	const options: MenuOption[] = [];

	// Cancel is only available while the subscription is not already canceled (mirrors contractMenuOptions).
	if (row?.state !== ContractState.CANCELED) {
		options.push({
			title: t('cancel'),
			onPress: () => {
				subscriptionId && cancelContract(subscriptionId, dispatch, onRefresh);
			},
			iconType: 'error_icon',
			color: 'destructive',
		});
	}

	options.push({
		title: t('deleteSubscription'),
		onPress: () => {
			deleteContract(subscriptionId, dispatch, onRefresh, onDelete);
		},
		iconType: 'delete',
		color: 'destructive',
	});

	return options;
};

export const deleteSubscription = async (subscriptionId: string, dispatch: Dispatch, callback?: () => void, onDelete?: () => void) => {
	try {
		await httpService({
			dispatch,
			path: PathsConfig.deleteAssignedSubscription,
			urlParam: { id: subscriptionId },
		});
		onDelete?.();
		callback?.();
	} catch (error) {
		dispatch(setOpenSuccessErrorModal({ responseError: error }));
	}
};
